import React, { useEffect, useState, forwardRef } from 'react'
import { Popover, Transition } from '@headlessui/react'
import { ChevronDownIcon } from '@heroicons/react/solid'
import type { NavItemGroup, NavItem } from './nav'
import { classNames } from 'src/utils'
import { Fragment } from 'react'
import { Link } from 'gatsby'
import resolveLink from 'src/utils/resolveLink'
import { MultilinkStoryblok } from '@/components/component-types-sb'

export interface TwoColumnsWithAnImageBlok {
  manu_label: string
  first_column_item: NavItemGroup
  first_column_label: string
  first_column_image: string
  second_column_items: NavItemGroup[]
  second_column_label: string
  link: MultilinkStoryblok
}

export interface TwoColumnsWithAnImageProps {
  blok: TwoColumnsWithAnImageBlok
  setMenuOpen: (menuOpen: boolean) => void
  menuOpen: boolean
  location: Location
  noHeroScrolled: boolean
}

export const TwoColumnsWithAnImage = ({
  blok,
  setMenuOpen,
  menuOpen,
  location,
  noHeroScrolled,
}: TwoColumnsWithAnImageProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const isCurrentPageOrParent = location.pathname.includes('')
  let timeout: NodeJS.Timeout

  const handleMouseOver = () => {
    clearTimeout(timeout)
    setIsOpen(true)
  }

  const handleMouseOut = () => {
    timeout = setTimeout(() => {
      setIsOpen(false)
    }, 50)
  }
  return (
    <div
      className="relative"
      onMouseOver={handleMouseOver}
      onMouseLeave={handleMouseOut}
    >
      <Link to={resolveLink(blok.link)}>
        <button
          className={classNames(
            isOpen || isCurrentPageOrParent ? 'text-darkblue' : 'text-darkblue',
            // If there is a hero on the page and user didn't scroll or there is an open menu
            noHeroScrolled ? '' : 'hover:!text-darkblue !text-darkblue',
            // menuOpen - if any menu is open
            // open - if the current menu is open
            isOpen
              ? 'text-darkblue rounded-full py-2 bg-gray-50 px-4 h-2'
              : !isOpen
                ? '!text-darkblue hover:!text-brightgreen'
                : '',
            'font-display text-[0.8rem] font-semibold group hover:text-brightgreen inline-flex cursor-pointer items-center justify-between py-4 leading-7 outline-none duration-300 focus:outline-none',
          )}
        >
          {blok.menu_label}
          <div className="h-5 w-4 ml-2">
            <ChevronDownIcon
              className={classNames(
                isOpen || isCurrentPageOrParent
                  ? 'text-darkblue'
                  : 'text-darkblue group-hover:!text-brightgreen',
                noHeroScrolled
                  ? 'text-darkblue group-hover:!text-brightgreen'
                  : '!text-darkblue group-hover:!text-darkblue',
                isOpen
                  ? '!text-darkblue group-hover:!text-brightgreen'
                  : !isOpen
                    ? '!text-darkblue group-hover:!text-brightgreen'
                    : '',
                'group-hover:!text-brightgreen mt-[1px] h-5 w-4 duration-300',
              )}
              aria-hidden="true"
            />
          </div>
        </button>
      </Link>
      <Transition
        as="div"
        show={isOpen}
        enter="transition ease-out duration-200"
        enterFrom="opacity-0 -translate-y-1"
        enterTo="opacity-100 translate-y-0"
        leave="transition ease-in duration-150"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 -translate-y-1"
        className="bg-white absolute top-full z-50 hidden transform rounded-3xl shadow-lg lg:block -ml-14 p-8 w-svw max-w-5xl mt-4"
      >
        <DropdownComponent {...blok} />
      </Transition>
    </div>
  )
}

const DropdownComponent = (items: TwoColumnsWithAnImageBlok) => {
  return (
    <div className="grid grid-cols-[19rem,1fr] w-screen max-w-max">
      <div className="font-display   ">
        <span className="font-medium text-darkblue">
          {items.first_column_label}
        </span>
        <div className="grid grid-cols-1 justify-start">
          {Object.values(items.first_column_item).map(
            // @ts-ignore: @TODO: fix the type error on NavItemGroup
            (item: NavItemGroup, idx) => {
              return (
                <div key={idx} className="grid grid-cols-1 gap-4 mt-2">
                  <Link
                    className="text-xs uppercase text-brightgreen grid grid-cols-1 space-y-4 hover:text-brightgreen/50"
                    to={resolveLink(item.group_label_link)}
                  >
                    {item.group_label}
                  </Link>
                  {item.group_links &&
                    item.group_links.map((subItem: NavItem, subIdx) => {
                      return (
                        <div key={subIdx}>
                          <Link
                            className="bg-gray-100 text-xs text-darkblue hover:text-brightgreen rounded-full py-2 px-4"
                            to={resolveLink(subItem.link)}
                          >
                            {subItem.text}
                          </Link>
                        </div>
                      )
                    })}
                </div>
              )
            },
          )}
          <div className="mt-4">
            <img
              className="max-w-[250px] max-h-[160px] object-contain"
              src={items.first_column_image}
              alt={items.first_column_label}
            />
          </div>
        </div>
      </div>
      <div className="font-display">
        <span className=" font-medium text-darkblue">
          {items.second_column_label}
        </span>
        <div className="flex items-start justify-between gap-4">
          {Object.values(items.second_column_items).map(
            // @ts-ignore: @TODO: fix the type error on NavItemGroup
            (item: NavItemGroup, idx) => {
              return (
                <div key={idx} className="grid grid-cols-1 gap-4 mt-2">
                  <Link
                    className="text-xs uppercase text-brightgreen grid grid-cols-1 space-y-4 hover:text-brightgreen/50"
                    to={resolveLink(item.group_label_link)}
                  >
                    {item.group_label}
                  </Link>
                  {item.group_links &&
                    item.group_links.map((subItem: NavItem, subIdx) => {
                      return (
                        <div key={subIdx}>
                          <Link
                            className="bg-gray-100 text-xs text-darkblue hover:text-brightgreen rounded-full py-2 px-4"
                            to={resolveLink(subItem.link)}
                          >
                            {subItem.text}
                          </Link>
                        </div>
                      )
                    })}
                </div>
              )
            },
          )}
        </div>
      </div>
    </div>
  )
}

TwoColumnsWithAnImage.displayName = 'Two Columns With An Image Block'
